import type { QuestionSetListQuery } from '@/api/questionSets';

/**
 * **The address contract of §6.9 — the sets list at `/questions`, and one set at
 * `/questions/sets/:setId`.**
 *
 * ```
 * /questions?page=2
 * /questions/sets/12
 * /questions/sets/12?archived=1
 * ```
 *
 * | In the address bar | On the wire | Why |
 * |---|---|---|
 * | `page` (the list) | `page` | the sets list is paginated — TASK-146's decision, below |
 * | `archived` (one set) | **nothing** (`include_archived` is always `true`) | the composition is held whole — `questionSetQuery` |
 *
 * ⚠️ **`?tab=` is gone, and with it every parameter the library tab owned** — `category`,
 * `difficulty`, and the two tab values (2026-09-24, ADR 0019). §6.9 was two tabs at one address from
 * 2026-08-12; the user looked at the shipped screen and answered that the *Questions* tab should not
 * exist — *"there should be only question sets, and adding, removing and so on should be done inside
 * each set"* (translated; ADR 0019 quotes the original) — and a set now owns its questions outright,
 * so there is nothing tenant-wide left to list. `/questions` **is** the sets list, which is what the bare address already answered for
 * `?tab=sets` and what §6.2's setup path has always pointed at.
 *
 * ⚠️ **A stale bookmark degrades rather than breaks, and that is this module's job rather than the
 * screen's.** `/questions?tab=questions&category=TECHNICAL&page=3` renders the sets list on page 3:
 * a parameter nothing reads is simply not read, which is the same rule an unrecognised **value**
 * already had (§6.3's *"dropped rather than sent"*). Nothing rewrites the address from a payload —
 * see the `page` note in `readSetsFilters`.
 *
 * ⚠️ **The one-press-one-history-entry guard went with the tabs, and it is recorded here rather than
 * silently dropped.** Radix's `Tabs.Trigger` calls `onValueChange` from two native handlers of one
 * pointer activation (`onMouseDown` unconditionally, and `onFocus` while the trigger is not yet
 * selected), so a single click on *Sets* pushed the same address **twice** and the first Back went
 * nowhere — a `ui-reviewer` live-pass finding on 2026-08-12, fixed with a ref holding the tab already
 * written. There is no tab, no `onValueChange` and no second push: the guard has nothing left to
 * guard, so it is **deleted with its subject** rather than kept as a precaution against a component
 * this screen no longer renders. The finding itself is not retracted — it is Radix's behaviour and it
 * is still true of any future `Tabs` caller, which is why it is written down here and in §6.9.
 *
 * ⚠️ **Until Sprint 19 this module held one constant and the filters lived in
 * `pages/Vacancies/vacancyUrls.ts`.** They were there because §6.9's address was
 * `/vacancies/:vacancyId/questions` and that module's stated reason for owning it was that *"both
 * live under one segment, so both live in one module"*. ADR 0018 took the vacancy out of the address;
 * `vacancyUrls.ts` keeps `/vacancies` and its `?page=` and spells no question address at all.
 *
 * ⚠️ **There is deliberately no `questionsUrl()` builder.** Nothing links *into* this screen under a
 * parameter: §6.2's setup path points at the bare address and §6.8's question count stopped being a
 * link in Sprint 19 (the address it pointed at no longer exists). A builder written today would be an
 * export with no caller — Sprint 8's `applyTenantAccent`, which shipped with a green unit test and
 * nothing calling it and was refused twice before being deleted. `QUESTIONS_PATH` and `setsSearch()`
 * are what a caller would compose one from, and it belongs here the moment it has one.
 *
 * ⚠️ **`src/app/navItems.ts` still writes the address as a literal, and that is not this constant
 * failing to be used.** §5's table is the app's own declaration of which addresses exist — the route
 * table is derived from it — and it spells all five of them rather than importing four page modules
 * into `src/app/`.
 */

/** The screen's own address, so a caller building a link does not restate it. */
export const QUESTIONS_PATH = '/questions';

/**
 * One set's own address — §6.9's composition screen, at `/questions/sets/:setId`.
 *
 * ⚠️ **Under `/questions` rather than at `/sets`, and that is §6.9 being one screen family rather than
 * two.** A set is what `/questions` lists, and a top-level `/sets` would be a sixth address §5's table
 * does not have — which is the mistake `/vacancies/:vacancyId/questions` made in the other direction.
 * The id is the **set's**, and it is the only path parameter this family has.
 */
export const QUESTION_SET_PATH = '/questions/sets/:setId';

/** `/questions/sets/12` — every link into the composition screen is built here (the href guard). */
export const questionSetUrl = (setId: number) => `${QUESTIONS_PATH}/sets/${setId}`;

/**
 * The query parameter names §6.9 owns — as data, so a rename is one edit and cannot half-happen.
 *
 * `archived` rather than `include_archived`: the address bar speaks the screen's language and the
 * wire's name never appears in it, the same separation `?vacancy=` ↔ `role_id=` makes in
 * `candidateFilters.ts`. It also cannot be confused with what goes out, because it **never** goes out
 * — the composition is read whole (`questionSetQuery`). `page` is the exception and says so: it is the
 * one parameter here whose name and value reach the wire unchanged.
 */
export const QUESTION_URL_PARAMS = {
  page: 'page',
  archived: 'archived',
} as const;

/** The one value `?archived=` has. Anything else is *off*, which is §6.9's default. */
const ARCHIVED_ON = '1';

/**
 * ⚠️ **The page size §6.9 asks the sets list for, and the one number in this module with nothing
 * behind it.**
 *
 * `NINJA_MAX_PER_PAGE_SIZE = 100` (`backend/src/project/settings/base.py`) is the ceiling django-ninja
 * silently clamps `?page_size=` to, and the OpenAPI document declares **no `maximum`** for the
 * parameter (`anyOf: integer(minimum 1) | null`). So this constant is checked by nothing: a test
 * asserting it equals the server's cap would be asserting against itself. What is checkable, and is
 * checked (`questionBank.test.tsx`), is two things beside it: that the client really sends this
 * number, and that the document declares no maximum **today**.
 *
 * ⚠️ **That second assertion is a record of what the document says, not a tripwire, and this line
 * claimed otherwise until BUG-070** (2026-08-09). It reads `openapi.snapshot.json`, a **committed file
 * this repository owns**, so a bound published in the other repository is invisible here until
 * somebody refreshes the snapshot — and whoever refreshes it is already doing this work.
 *
 * ⚠️ **TASK-146's decision, taken here because this module is the address contract it lives in: the
 * list is *paged*.** `?page=` in the address, a range line, both ends disabled, and the page **not
 * clamped** from the response. It applied to two lists when this screen had two; the library is gone
 * and the argument is unchanged for the one that remains — a tenant-wide list with a server `count`,
 * growing by ordinary use. ⚠️ **`LIBRARY_PAGE_SIZE` stood beside this constant and is deleted with the
 * route it sized** (2026-09-24). The paragraphs that argued *server-side filtering was refused* went
 * with it: there are no filters on this list, because a set row has a name, a count, an author and a
 * state and none of them is a vocabulary to narrow by.
 */
export const SETS_PAGE_SIZE = 50;

/** What the address bar says about the sets list, after validation. */
export interface SetsListFilters {
  /** One-based, as the API counts pages. */
  page: number;
}

/** What a caller building a link or a patch may say. Anything omitted is left out of the address. */
export interface SetsListInput {
  page?: number;
}

/** What the address bar says about one set's composition, after validation. */
export interface SetFilters {
  /** §6.9: *"a Show archived toggle, off by default."* — now the **set's**, not the library's. */
  showArchived: boolean;
}

export interface SetInput {
  showArchived?: boolean;
}

/** A positive integer, or nothing. `?page=0`, `?page=-2` and `?page=two` are all "nothing". */
function positiveInteger(raw: string | null): number | null {
  if (raw === null || raw.trim() === '') return null;
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 ? value : null;
}

/**
 * Reads the sets list's own parameter out of the address bar. **Never throws and never produces a
 * value the API would refuse.**
 *
 * ⚠️ **A `page` past the end is *kept*, not clamped** — §6.8's own rule, inherited rather than
 * re-derived: `count` is only known once the answer arrives, so clamping would make the screen rewrite
 * the address from a payload, which is a second writer of the page and a history entry nobody
 * navigated to. The address stays what was typed and the screen says the page is empty.
 */
export function readSetsFilters(params: URLSearchParams): SetsListFilters {
  return { page: positiveInteger(params.get(QUESTION_URL_PARAMS.page)) ?? 1 };
}

/**
 * The query string for the sets list — what `setSearchParams` is given. A parameter carrying nothing
 * is **absent**, not empty, so `/questions` and `/questions?page=1` are never two addresses for one
 * screen and §6.2's setup path keeps pointing at the bare one.
 */
export function setsSearch(filters: SetsListInput = {}): string {
  const params = new URLSearchParams();
  if (filters.page != null && filters.page > 1) {
    params.set(QUESTION_URL_PARAMS.page, String(filters.page));
  }
  return params.toString();
}

/**
 * Reads one set screen's *Show archived* out of the address bar, so a composition showing its archived
 * rows survives a reload, is linkable, and is undone by Back — §6.3's contract, applied to the screen
 * that inherited the toggle.
 */
export function readSetFilters(params: URLSearchParams): SetFilters {
  return { showArchived: params.get(QUESTION_URL_PARAMS.archived) === ARCHIVED_ON };
}

/**
 * *Show archived* off is the **absence** of `archived` rather than `archived=0`: §6.9's default is not
 * something to write into an address people copy to each other.
 */
export function setSearch(filters: SetInput = {}): string {
  const params = new URLSearchParams();
  if (filters.showArchived) params.set(QUESTION_URL_PARAMS.archived, ARCHIVED_ON);
  return params.toString();
}

/**
 * The query `GET /api/v1/question-sets/` is asked with.
 *
 * ⚠️ **`include_retired: true`, always.** *Show retired* is a control §6.9's set list does not have;
 * what the list needs the flag for is **Restore**, which has to be able to find its target, and a
 * retired set that vanished the moment it was retired would be a state with no way back into it — the
 * defect TASK-114 shipped on the staff surface and TASK-115 had to come back for. The row says which
 * it is in words (§3).
 *
 * ⚠️ **No `?author_id=` here.** The parameter exists for §6.5's pre-fill (ADR 0016 decision 5(b)) and
 * a *"my sets"* filter is not on this screen: a parameter without a caller is the rule BUG-048 set.
 */
export function toQuestionSetListQuery(filters: SetsListFilters): QuestionSetListQuery {
  return {
    include_retired: true,
    page_size: SETS_PAGE_SIZE,
    ...(filters.page > 1 ? { page: filters.page } : {}),
  };
}
